import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';

const ExportDispatchModal = ({
  show,
  onClose,
  formData,
  handleInputChange,
  handleSubmit,
  addContainer,
  removeContainer,
  addIntermediateDestination,
  removeIntermediateDestination,
  shippingLines,
  isLoading,
}) => {
  const { t } = useTranslation();

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4">
      <div
        className="rounded-xl shadow-xl text-white p-6 relative w-full max-w-md max-h-[90vh] overflow-y-auto hide-scrollbar"
        style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl font-bold hover:text-gray-300"
          aria-label={t('alt.closeExportModal')}
        >
          <FiX />
        </button>

        <h2 className="text-xl font-bold mb-4">{t('orders.createExportDispatch')}</h2>

        <form onSubmit={(e) => handleSubmit(e, 'Export')} className="space-y-4">
          {/* Shipping Line */}
          <div>
            <label className="text-sm block mb-1">{t('orders.shippingLine')}</label>
            <select
              name="sl"
              value={formData.export.sl}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            >
              <option value="">{t('orders.selectShippingLine')}</option>
              {shippingLines.map((line) => (
                <option key={line.id} value={line.name}>{line.name}</option>
              ))}
            </select>
          </div>

          {/* Load Date */}
          <div>
            <label className="text-sm block mb-1">{t('orders.loadDate')}</label>
            <input
              type="date"
              name="loadDate"
              value={formData.export.loadDate}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            />
          </div>

          {/* Load Time */}
          <div>
            <label className="text-sm block mb-1">{t('orders.loadTime')}</label>
            <input
              type="time"
              name="loadTime"
              value={formData.export.loadTime}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            />
          </div>

          {/* Load Address */}
          <div>
            <label className="text-sm block mb-1">{t('orders.loadAddress')}</label>
            <input
              type="text"
              name="loadAddress"
              value={formData.export.loadAddress}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              placeholder={t('orders.loadAddressPlaceholder')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            />
          </div>

          {/* Destination */}
          <div>
            <label className="text-sm block mb-1">{t('orders.destination')}</label>
            <input
              type="text"
              name="destination"
              value={formData.export.destination}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              placeholder={t('orders.destinationPlaceholder')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            />
          </div>

          {/* Booking Number */}
          <div>
            <label className="text-sm block mb-1">{t('orders.bookingNumbers')}</label>
            <input
              type="text"
              name="bookingNumber"
              value={formData.export.bookingNumber}
              onChange={(e) => handleInputChange(e, null, 'Export')}
              placeholder={t('orders.bookingNumberPlaceholders')}
              className="w-full px-3 py-2 rounded bg-white text-black"
            />
          </div>

          {/* Containers */}
          <div>
            <label className="text-sm block mb-1">{t('orders.containers')}</label>
            {formData.export.containers.map((container, index) => (
              <div key={index} className="space-y-2 mb-2 p-2 border border-gray-300 rounded">
                {[
                  { field: 'poNumber', label: t('orders.bookingNumber'), placeholder: t('orders.bookingNumberPlaceholder') },
                  { field: 'size', label: t('orders.size'), placeholder: t('orders.selectSize') },
                  { field: 'pickEmpty', label: t('orders.pickEmpty'), placeholder: t('orders.pickEmptyPlaceholder') },
                  { field: 'commodity', label: t('orders.commodity'), placeholder: t('orders.commodityPlaceholder') },
                  { field: 'returnFull', label: t('orders.returnFull'), placeholder: t('orders.returnFullPlaceholder') },
                  { field: 'appointmentPin', label: t('orders.appointmentPin'), placeholder: t('orders.appointmentPinPlaceholder') },
                ].map(({ field, label, placeholder }) => (
                  <div key={field}>
                    <label className="text-sm block mb-1">{label}</label>
                    <input
                      type="text"
                      name={`containers.${index}.${field}`}
                      value={container[field] || ''}
                      onChange={(e) => handleInputChange(e, index, 'Export')}
                      placeholder={placeholder}
                      className="w-full px-3 py-2 rounded bg-white text-black"
                    />
                  </div>
                ))}
                {formData.export.containers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeContainer(index)}
                    className="text-red-400 hover:text-red-600 mt-2"
                  >
                    {t('orders.removeContainer')}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addContainer}
              className="text-blue-400 hover:text-blue-600 mt-2"
            >
              {t('orders.addContainer')}
            </button>
          </div>

          {/* Intermediate Stops */}
          <div>
            <label className="text-sm block mb-1">{t('orders.intermediateStops')}</label>
            {formData.export.intermediateDestinations.map((stop, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  name={`intermediateDestinations.${index}.destination`}
                  value={stop.destination}
                  onChange={(e) => handleInputChange(e, index, 'Export')}
                  placeholder={t('orders.stopDestinationPlaceholder', { index: index + 1 })}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                />
                <input
                  type="time"
                  name={`intermediateDestinations.${index}.time`}
                  value={stop.time}
                  onChange={(e) => handleInputChange(e, index, 'Export')}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                />
                {formData.export.intermediateDestinations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeIntermediateDestination(index, 'Export')}
                    className="text-red-400 hover:text-red-600"
                  >
                    {t('orders.removeStop')}
                  </button>
                )}
              </div>
            ))}
            {formData.export.intermediateDestinations.length < 15 && (
              <button
                type="button"
                onClick={() => addIntermediateDestination('Export')}
                className="text-blue-400 hover:text-blue-600 mt-2"
              >
                {t('orders.addStop')}
              </button>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-[#792aff] text-white py-2 rounded-md font-semibold"
            disabled={isLoading}
          >
            {isLoading ? t('dashboard.loading') : t('orders.createExportDispatch')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExportDispatchModal;